import React from "react";
import ReactECharts from "echarts-for-react";
import { buildGanttSegments } from "../../utils/gantt.js";

// Convertit un nombre "jour de l'année" (0 = 1er janvier) en date lisible.
// Cohérent avec dayIndex() dans utils/gantt.js (même référence Jan. 1er).
function dayNumberToLabel(dayNum) {
  const d = new Date(2023, 0, 1 + Math.round(dayNum));
  return d.toLocaleDateString("fr-FR", { day: "2-digit", month: "short" });
}

// Couleur de fond de l'étiquette d'écart, selon l'horizon (RCP classique ou
// TRACC) — l'horizon de référence (index 0) n'a jamais d'étiquette, donc pas
// besoin d'entrée ici.
const BG_ECART_HORIZON = {
  2035: "#7b2cbf",
  2050: "#cc1100",
  "+2": "#FDAE61",
  "+2.7": "#EA5733",
};

export default function PhenoGanttChart({ data }) {
  const { rows, colorByEtape } = buildGanttSegments(data);
  const maxSegments = Math.max(0, ...rows.map((r) => r.segments.length));

  const etapesOrdonnees = Array.from({ length: maxSegments }).map(
    (_, i) => rows[0]?.segments[i]?.etape
  );

  const categories = rows.map((r) => String(r.YearGroup));

  const debuts = rows.map((r) => r.offset);
  const fins = rows.map((r) => r.offset + r.segments.reduce((acc, s) => acc + s.duree, 0));
  const domainMin = Math.max(0, Math.min(...debuts) - 5);
  const domainMax = Math.max(...fins) + 5;

  // "rows" est déjà ordonné du plus proche au plus sévère (2020/2035/2050
  // ou reference/+2/+2.7 selon le mode) — l'horizon de référence est donc
  // toujours le premier, pas besoin de chercher "2020" en dur.
  const finBase = fins[0];
  const ecartData = rows
    .map((r, i) => {
      if (i === 0 || finBase == null) return null;
      const ecart = Math.round(fins[i] - finBase);
      const bg = BG_ECART_HORIZON[r.YearGroup] || "#cc1100";
      return {
        value: [fins[i], categories[i]],
        ecart,
        label: {
          color: "#fff",
          backgroundColor: bg,
          padding: [4, 8],
          borderRadius: 4,
        },
      };
    })
    .filter(Boolean);

  const series = [
    {
      name: "offset",
      type: "bar",
      stack: "total",
      data: rows.map((r) => r.offset),
      itemStyle: { color: "transparent" },
      tooltip: { show: false },
      silent: true,
    },
    ...Array.from({ length: maxSegments }).map((_, i) => ({
      name: etapesOrdonnees[i] || `Étape ${i + 1}`,
      type: "bar",
      stack: "total",
      barMaxWidth: 60,
      data: rows.map((r) => r.segments[i]?.duree ?? 0),
      itemStyle: {
        color: colorByEtape[etapesOrdonnees[i]] || "#ccc",
        borderColor: "#fff",
        borderWidth: 2,
      },
      label: {
        show: true,
        position: "inside",
        fontSize: 13,
        fontWeight: "bold",
        color: rows[0]?.segments[i]?.colorTexte || "#111",
      },
    })),
    {
      name: "ecart_vs_reference",
      type: "scatter",
      data: ecartData,
      symbolSize: 0,
      silent: true,
      tooltip: { show: false },
      label: {
        show: true,
        position: "right",
        offset: [8, 0],
        fontSize: 16,
        fontWeight: "bold",
        color: "#333",
        formatter: (p) => `${p.data.ecart > 0 ? "+" : ""}${p.data.ecart} j`,
      },
    },
  ];

  const option = {
    legend: {
      bottom: 0,
      type: "plain",
      data: etapesOrdonnees.filter(Boolean),
      textStyle: { fontSize: 16 },
    },
    grid: { left: 40, right: 0, top: 20, height: 290, containLabel: true },
    xAxis: { type: "value", min: domainMin, max: domainMax, axisLabel: { formatter: dayNumberToLabel } },
    yAxis: { type: "category", data: categories, inverse: true },
    series,
  };

  return <ReactECharts option={option} style={{ height: 480 }} notMerge lazyUpdate />;
}